import * as Dfns from 'date-fns/fp';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import cn from 'classnames';
import { Line, LineChart, ReferenceLine, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import { TICK_STYLE, X_AXIS_PADDING } from '@Components/Charts/ChartConstants';
import { FilterBar } from '@Components/FilterBar';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { EmptyState } from '@Components/EmptyState';
import { Placement } from '@Components/designConstants';
import { TruncatedTextTooltip } from '@Components/TruncatedTextTooltip';
import { useSetError } from '@src/redux/modals';
import { ClearanceTooltip } from './components/ClearanceTooltip';
import { CsvDownloadButton } from './components/CsvDownloadButton';
import { NetworkLabel } from './components/PacingLabels';
import { LoadFailedState, RowsSkeleton } from './components/PacingSkeletons';
import {
  CLEARANCE_CHART_COLORS,
  CLEARANCE_CHART_MARGIN,
  CLEARANCE_HIGH_REFERENCE,
  CLEARANCE_LOW_REFERENCE
} from './linear-pacing-constants';
import { ClearanceRow, createSeriesData, SeriesData } from './linear-pacing-utils';
import { buildWeekRange, useClearance } from './use-linear-clearance';

interface NetworkLine {
  network: string;
}

const NETWORK_FILTER_OPTIONS = ['network'];

const formatWeekTick = (week: string) => Dfns.format('MM/dd', Dfns.parseISO(week));
const formatPercent = (val: unknown) => `${val}%`;

// The y-axis floor: the lowest clearance rate (capped at the low reference line), stepped down to
// the multiple of 10 below it.
const getMinY = (series: ClearanceRow[]) => {
  const rates = series
    .map(row => row.clearance_rate)
    .filter((rate): rate is number => Number.isFinite(rate));
  const minY = Math.min(CLEARANCE_LOW_REFERENCE, ...rates);
  return minY % 10 === 0 ? minY - 10 : minY - (minY % 10);
};

const ClearanceChart = ({ series, range }: { series: ClearanceRow[]; range: string[] }) => {
  const minY = getMinY(series);
  return (
    <div className='clearance-chart'>
      <AutoSizer
        renderProp={({ width = 0, height = 0 }) => (
          <LineChart width={width} height={height} data={series} margin={CLEARANCE_CHART_MARGIN}>
            <XAxis
              dataKey='week'
              ticks={range}
              tickCount={range.length}
              axisLine={false}
              tickLine={false}
              padding={X_AXIS_PADDING}
              tick={TICK_STYLE}
              tickFormatter={formatWeekTick}
              domain={[range[0], range[range.length - 1]]}
            />
            <YAxis
              tick={TICK_STYLE}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatPercent}
              domain={[minY, 100]}
            />
            <Line
              type='monotone'
              dataKey='clearance_rate'
              isAnimationActive={false}
              name='Clearance Rate'
              stroke={CLEARANCE_CHART_COLORS.clearance}
            />
            <RechartsTooltip isAnimationActive={false} content={<ClearanceTooltip />} />
            <ReferenceLine
              y={CLEARANCE_LOW_REFERENCE}
              stroke={CLEARANCE_CHART_COLORS.lowReference}
              strokeDasharray='4'
            />
            <ReferenceLine
              y={CLEARANCE_HIGH_REFERENCE}
              stroke={CLEARANCE_CHART_COLORS.highReference}
              strokeDasharray='4'
            />
          </LineChart>
        )}
      />
    </div>
  );
};

const ClearanceSummary = ({ itemKey, seriesData }: { itemKey: string; seriesData: SeriesData }) => (
  <OverlayTrigger
    placement={Placement.TOP}
    overlay={<Tooltip id={`summary_${itemKey}`}>Gross Booked / Cumulative Clearance</Tooltip>}
  >
    <div className='clearance-label'>
      {seriesData.spendMap[itemKey]} / {seriesData.clearanceMap[itemKey]}
    </div>
  </OverlayTrigger>
);

// One row per item and week, as the charts plot them, in the panes' order.
const clearanceCsvRows = (label: string, keys: string[], seriesData: SeriesData): unknown[][] => [
  [label, 'Week', 'Clearance Rate', 'Gross Booked', 'Gross Cleared', 'Spots Booked', 'Spots Aired'],
  ...keys.flatMap(key =>
    seriesData.groupedByKey[key].map(row => [
      key,
      row.week,
      row.clearance_rate === null ? '' : `${row.clearance_rate}%`,
      row.spend_expected,
      row.spend_actual,
      row.count_expected,
      row.count_actual
    ])
  )
];

// Matches .clearance-row's height in LinearPacing.scss.
const CLEARANCE_ROW_HEIGHT = 160;

const RotationRows = ({
  seriesData,
  range
}: {
  seriesData: SeriesData | undefined;
  range: string[];
}) => {
  if (!seriesData) {
    return <RowsSkeleton rowHeight={CLEARANCE_ROW_HEIGHT} />;
  }
  return seriesData.keyOrder.map(rotation => (
    <div key={rotation} className='clearance-row'>
      <TruncatedTextTooltip text={`${rotation}:`} className='clearance-label rotation-label' />
      <ClearanceChart series={seriesData.groupedByKey[rotation]} range={range} />
      <ClearanceSummary itemKey={rotation} seriesData={seriesData} />
    </div>
  ));
};

const AvailRows = ({
  network,
  avails,
  seriesData,
  range,
  selectedAvail,
  onSelect
}: {
  network: string;
  avails: string[];
  seriesData: SeriesData;
  range: string[];
  selectedAvail?: string;
  onSelect: (avail?: string) => void;
}): ReactNode =>
  avails.map(avail => {
    const isSelected = avail === selectedAvail;
    return (
      <InteractiveDiv
        key={avail}
        className={cn('clearance-row', { selected: isSelected })}
        onClick={() => onSelect(isSelected ? undefined : avail)}
      >
        <NetworkLabel network={network} />
        <div className='clearance-label'>({avail})</div>
        <ClearanceChart series={seriesData.groupedByKey[avail]} range={range} />
        <ClearanceSummary itemKey={avail} seriesData={seriesData} />
      </InteractiveDiv>
    );
  });

export const LinearPacingClearance = ({
  startDate,
  endDate,
  selectedLeft: selectedNetwork,
  setSelectedLeft: setSelectedNetwork,
  selectedRight: selectedAvail,
  setSelectedRight: setSelectedAvail
}: {
  startDate: string;
  endDate: string;
  // The page's lifted selection, so the breadcrumb shows network > avail.
  selectedLeft?: string;
  setSelectedLeft: (network?: string) => void;
  selectedRight?: string;
  setSelectedRight: (avail?: string) => void;
}) => {
  const setError = useSetError();
  const [networkFilter, setNetworkFilter] = useState<(line: NetworkLine) => boolean>(
    () => () => true
  );

  const range = useMemo(() => buildWeekRange(startDate, endDate), [startDate, endDate]);

  const networksQuery = useClearance({ group: ['network'], start: startDate, end: endDate });
  const availsQuery = useClearance(
    { group: ['network', 'avail'], start: startDate, end: endDate, network: selectedNetwork },
    Boolean(selectedNetwork)
  );
  const rotationsQuery = useClearance(
    {
      group: ['network', 'avail', 'rotation'],
      start: startDate,
      end: endDate,
      network: selectedNetwork,
      avail: selectedAvail
    },
    Boolean(selectedNetwork && selectedAvail)
  );

  const error = networksQuery.error || availsQuery.error || rotationsQuery.error;
  useEffect(() => {
    if (error) {
      setError({ message: 'Could not fetch data for clearance page', reportError: error });
    }
  }, [error, setError]);

  const seriesData = useMemo(
    () => networksQuery.data && createSeriesData('network', networksQuery.data, range),
    [networksQuery.data, range]
  );
  const availSeriesData = useMemo(
    () => availsQuery.data && createSeriesData('avail', availsQuery.data, range),
    [availsQuery.data, range]
  );
  const rotationSeriesData = useMemo(
    () => rotationsQuery.data && createSeriesData('rotation', rotationsQuery.data, range),
    [rotationsQuery.data, range]
  );

  const networkLines = useMemo(
    () => (seriesData?.keyOrder ?? []).map(key => ({ network: key })),
    [seriesData]
  );
  // A selected network condenses the list to itself, as on Streaming Pacing.
  const visibleNetworks = useMemo(
    () =>
      selectedNetwork
        ? [selectedNetwork]
        : networkLines.filter(networkFilter).map(line => line.network),
    [networkLines, networkFilter, selectedNetwork]
  );
  const visibleAvails = useMemo(
    () => (selectedAvail ? [selectedAvail] : (availSeriesData?.keyOrder ?? [])),
    [availSeriesData, selectedAvail]
  );

  // Clear the drill-down when the selected network drops out of the date range.
  useEffect(() => {
    if (selectedNetwork && seriesData && !seriesData.keyOrder.includes(selectedNetwork)) {
      setSelectedAvail(undefined);
      setSelectedNetwork(undefined);
    }
  }, [seriesData, selectedNetwork, setSelectedAvail, setSelectedNetwork]);

  const selectNetwork = (clicked: string) => {
    setSelectedAvail(undefined);
    setSelectedNetwork(selectedNetwork === clicked ? undefined : clicked);
  };

  return (
    <div className='pacing-dashboard'>
      <div className='pacing-panes'>
        <div className={cn('pacing-left-pane', { condensed: selectedNetwork })}>
          <div className='pacing-pane'>
            {!selectedNetwork && (
              <>
                <div className='pacing-pane-title'>Networks</div>
                <div className='pacing-pane-controls'>
                  <FilterBar<NetworkLine>
                    hasAdvanced
                    options={NETWORK_FILTER_OPTIONS}
                    lines={networkLines}
                    onFilter={filter => setNetworkFilter(() => filter)}
                    storageKeySuffix='linear-pacing-clearance-networks'
                  />
                  {seriesData && visibleNetworks.length > 0 && (
                    <CsvDownloadButton
                      filename={`Linear clearance by network ${startDate} to ${endDate}`}
                      rows={() => clearanceCsvRows('Network', visibleNetworks, seriesData)}
                    />
                  )}
                </div>
              </>
            )}
            <div className='pacing-pane-lines'>
              {seriesData && visibleNetworks.length === 0 && (
                <EmptyState title='No clearance data for this date range' />
              )}
              {seriesData &&
                visibleNetworks.map(rowNetwork => (
                  <InteractiveDiv
                    key={rowNetwork}
                    onClick={() => selectNetwork(rowNetwork)}
                    className={cn('clearance-row', { selected: selectedNetwork === rowNetwork })}
                  >
                    <NetworkLabel network={rowNetwork} />
                    {!selectedNetwork && (
                      <>
                        <ClearanceChart
                          series={seriesData.groupedByKey[rowNetwork]}
                          range={range}
                        />
                        <ClearanceSummary itemKey={rowNetwork} seriesData={seriesData} />
                      </>
                    )}
                  </InteractiveDiv>
                ))}
              {!seriesData &&
                (error ? <LoadFailedState /> : <RowsSkeleton rowHeight={CLEARANCE_ROW_HEIGHT} />)}
            </div>
          </div>
        </div>
        {selectedNetwork && (
          <div className='pacing-right-pane'>
            <div
              className={cn('pacing-top-pane', 'clearance-top-pane', { condensed: selectedAvail })}
            >
              <div className='pacing-pane'>
                {!selectedAvail && (
                  <div className='pacing-pane-title'>Avails for {selectedNetwork}</div>
                )}
                {!selectedAvail && availSeriesData && visibleAvails.length > 0 && (
                  <div className='pacing-pane-controls align-end'>
                    <CsvDownloadButton
                      filename={`Linear clearance for ${selectedNetwork} ${startDate} to ${endDate}`}
                      rows={() => clearanceCsvRows('Avail', visibleAvails, availSeriesData)}
                    />
                  </div>
                )}
                <div className='pacing-pane-lines'>
                  {availSeriesData && (
                    <AvailRows
                      network={selectedNetwork}
                      avails={visibleAvails}
                      seriesData={availSeriesData}
                      range={range}
                      selectedAvail={selectedAvail}
                      onSelect={setSelectedAvail}
                    />
                  )}
                  {!availSeriesData &&
                    (availsQuery.error ? (
                      <LoadFailedState />
                    ) : (
                      <RowsSkeleton rowHeight={CLEARANCE_ROW_HEIGHT} />
                    ))}
                </div>
              </div>
            </div>
            {selectedAvail && (
              <div className='pacing-bottom-pane'>
                <div className='pacing-pane-lines'>
                  {rotationsQuery.error ? (
                    <LoadFailedState />
                  ) : (
                    <RotationRows seriesData={rotationSeriesData} range={range} />
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
